import { CommonModule } from '@angular/common';
import { Component, ElementRef, HostListener, inject, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { ToastrService } from 'ngx-toastr';
import { Subject } from 'rxjs';
import { FlatpickrDatePickerComponent } from '../../../../../../_shared/components/flatpickr-date-picker/flatpickr-date-picker.component';
import { ErrorHandlerService } from '../../../../../../_shared/services/error-handler.service';
import { VehicleDocumentsService } from '../../../../services/vehicle-documents.service';
import { AssetCompliance, VehicleDocumentType } from '../../../../models/vehicle-documents.model';
import { TranslocoDirective, TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import { LookupLabelPipe } from '../../../../../../_shared/pipes/lookup-label.pipe';
import { translateLookupLabel } from '../../../../../../_shared/helpers/lookup-label.helper';

@Component({
  selector: 'app-upload-compliance-document-modal',
  standalone: true,
  imports: [CommonModule, FormsModule, FlatpickrDatePickerComponent, TranslocoDirective, TranslocoPipe, LookupLabelPipe],
  templateUrl: './upload-compliance-document-modal.component.html',
  styleUrl: './upload-compliance-document-modal.component.scss'
})
export class UploadComplianceDocumentModalComponent implements OnInit, OnDestroy {
  @Input() assetId!: number;

  @ViewChild('complianceControl') complianceControl?: ElementRef<HTMLElement>;
  @ViewChild('documentTypeControl') documentTypeControl?: ElementRef<HTMLElement>;

  complianceOptions: AssetCompliance[] = [];
  documentTypes: VehicleDocumentType[] = [];
  selectedComplianceIds = new Set<number>();
  selectedDocumentType: VehicleDocumentType | null = null;

  file: File | null = null;
  fileName = '';
  reference = '';
  expiryDate = '';
  otherDocumentName = '';
  otherDocumentExpires = false;

  isLoadingOptions = true;
  isUploadingTemp = false;
  uploadSuccess = false;
  uploadProgress = 0;
  tempUploadFailed = false;
  isSaving = false;

  complianceDropdownOpen = false;
  documentTypeDropdownOpen = false;

  private readonly otherDocumentTypeName = 'Other';
  private readonly maxFileSizeInBytes = 5 * 1024 * 1024;
  /** Vehicle document type names are catalogue data; translated for display only. */
  protected readonly documentTypeLabelPrefix = 'assetOverview.documentTypeNames';
  private uploadInterval?: ReturnType<typeof setInterval>;

  private activeModal = inject(NgbActiveModal);
  private vehicleDocumentsService = inject(VehicleDocumentsService);
  private errorHandlerService = inject(ErrorHandlerService);
  private toastr = inject(ToastrService);
  private readonly transloco: TranslocoService = inject(TranslocoService);
  private cancellation = new Subject<void>();

  ngOnInit(): void {
    this.loadOptions();
  }

  ngOnDestroy(): void {
    this.clearUploadInterval();
    this.cancellation.next();
    this.cancellation.complete();
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    const target = event.target as Node;

    if (this.complianceDropdownOpen && this.complianceControl && !this.complianceControl.nativeElement.contains(target)) {
      this.complianceDropdownOpen = false;
    }

    if (this.documentTypeDropdownOpen && this.documentTypeControl && !this.documentTypeControl.nativeElement.contains(target)) {
      this.documentTypeDropdownOpen = false;
    }
  }

  get selectedCompliances(): AssetCompliance[] {
    return this.complianceOptions.filter(compliance => this.selectedComplianceIds.has(compliance.complianceId));
  }

  get documentTypeLabel(): string {
    return this.selectedDocumentType
      ? translateLookupLabel(this.transloco, this.documentTypeLabelPrefix, this.selectedDocumentType.name)
      : this.transloco.translate('assetOverview.uploadDocumentModal.selectDocumentType');
  }

  get expiryRequired(): boolean {
    return this.selectedDocumentType?.validUntilRequired ?? false;
  }

  get isOtherSelected(): boolean {
    return this.selectedDocumentType?.name === this.otherDocumentTypeName;
  }

  /** "Other" is reserved: uploading Other-as-"Other" would leave the document untyped. */
  get isOtherNameReserved(): boolean {
    return this.isOtherSelected
      && this.otherDocumentName.trim().toLowerCase() === this.otherDocumentTypeName.toLowerCase();
  }

  get showExpiryDate(): boolean {
    return this.isOtherSelected ? this.otherDocumentExpires : this.expiryRequired;
  }

  get expiryDateRequired(): boolean {
    return this.isOtherSelected ? this.otherDocumentExpires : this.expiryRequired;
  }

  get canSave(): boolean {
    return this.selectedComplianceIds.size > 0
      && !!this.selectedDocumentType
      && (!this.isOtherSelected || !!this.otherDocumentName.trim())
      && !this.isOtherNameReserved
      && this.uploadSuccess
      && (!this.expiryDateRequired || !!this.expiryDate)
      && !this.isSaving;
  }

  toggleComplianceDropdown(): void {
    if (this.isLoadingOptions) return;
    this.documentTypeDropdownOpen = false;
    this.complianceDropdownOpen = !this.complianceDropdownOpen;
  }

  toggleDocumentTypeDropdown(): void {
    if (this.isLoadingOptions) return;
    this.complianceDropdownOpen = false;
    this.documentTypeDropdownOpen = !this.documentTypeDropdownOpen;
  }

  isComplianceSelected(compliance: AssetCompliance): boolean {
    return this.selectedComplianceIds.has(compliance.complianceId);
  }

  onPillListWheel(event: WheelEvent): void {
    const list = event.currentTarget as HTMLElement;
    if (list.scrollWidth <= list.clientWidth || event.deltaY === 0) return;

    list.scrollLeft += event.deltaY;
    event.preventDefault();
  }

  toggleCompliance(compliance: AssetCompliance): void {
    if (this.selectedComplianceIds.has(compliance.complianceId)) {
      this.selectedComplianceIds.delete(compliance.complianceId);
    } else {
      this.selectedComplianceIds.add(compliance.complianceId);
    }
  }

  selectDocumentType(documentType: VehicleDocumentType): void {
    if (documentType.alreadyUploaded) {
      this.toastr.error(this.transloco.translate('assetOverview.uploadDocumentModal.toasts.duplicateType', {
        name: translateLookupLabel(this.transloco, this.documentTypeLabelPrefix, documentType.name)
      }));
    } else {
      this.selectedDocumentType = documentType;
      this.documentTypeDropdownOpen = false;
      this.otherDocumentName = '';
      this.otherDocumentExpires = false;
      this.expiryDate = '';
    }
  }

  onExpiryChange(value: string | undefined): void {
    this.expiryDate = value ?? '';
  }

  onExpiryToggleChange(): void {
    if (!this.otherDocumentExpires) {
      this.expiryDate = '';
    }
  }

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (!input.files || input.files.length === 0) return;

    const selectedFile = input.files[0];
    if (selectedFile.size > this.maxFileSizeInBytes) {
      this.toastr.error(this.transloco.translate('assetOverview.uploadDocumentModal.toasts.fileTooLarge'));
      input.value = '';
      return;
    }

    this.file = selectedFile;
    this.fileName = selectedFile.name;
    this.reference = crypto.randomUUID();
    input.value = '';

    this.uploadTempDocument();
  }

  removeFile(): void {
    this.clearUploadInterval();
    this.file = null;
    this.fileName = '';
    this.reference = '';
    this.uploadProgress = 0;
    this.isUploadingTemp = false;
    this.uploadSuccess = false;
    this.tempUploadFailed = false;
  }

  async save(): Promise<void> {
    if (!this.canSave) return;

    this.isSaving = true;

    try {
      await this.vehicleDocumentsService.uploadDocument({
        assetId: this.assetId,
        reference: this.reference,
        documentName: this.getDocumentName(),
        extension: this.getFileExtension(),
        documentTypeId: this.selectedDocumentType!.id,
        validUntil: this.showExpiryDate ? (this.expiryDate || null) : null,
        complianceIds: [...this.selectedComplianceIds]
      });

      this.toastr.success(this.transloco.translate('assetOverview.uploadDocumentModal.toasts.uploaded'));
      this.activeModal.close(true);
      this.isSaving = false;
    } catch (error: any) {
      this.errorHandlerService.handleError(error, {
        showToast: true,
        callback: () => {
          this.isSaving = false;
        }
      });
    }
  }

  closeModal(): void {
    this.activeModal.dismiss();
  }

  private async loadOptions(): Promise<void> {
    this.isLoadingOptions = true;

    try {
      const [compliancesResponse, documentTypesResponse] = await Promise.all([
        this.vehicleDocumentsService.getAssetCompliances(this.assetId, this.cancellation),
        this.vehicleDocumentsService.getDocumentTypesByAssetId(this.assetId, this.cancellation)
      ]);

      this.complianceOptions = compliancesResponse.data ?? [];
      this.documentTypes = documentTypesResponse.data ?? [];
      this.isLoadingOptions = false;
    } catch (error: any) {
      this.errorHandlerService.handleError(error, {
        showToast: true,
        callback: () => {
          this.isLoadingOptions = false;
        }
      });
    }
  }

  private uploadTempDocument(): void {
    if (!this.file) return;

    this.isUploadingTemp = true;
    this.uploadSuccess = false;
    this.tempUploadFailed = false;
    this.uploadProgress = 0;

    const formData = new FormData();
    formData.append('AssetId', this.assetId.toString());
    formData.append('Reference', this.reference);
    formData.append('Document', this.file);

    this.vehicleDocumentsService.uploadTempDocument(formData)
      .then(() => {
        this.uploadProgress = 100;
        setTimeout(() => {
          this.clearUploadInterval();
          this.isUploadingTemp = false;
          this.uploadSuccess = true;
        }, 500);
      })
      .catch((error: any) => {
        this.clearUploadInterval();
        this.tempUploadFailed = true;
        this.uploadProgress = 0;
        this.errorHandlerService.handleError(error, {
          showToast: true,
          callback: () => {
            this.isUploadingTemp = false;
          }
        });
      });

    this.uploadInterval = setInterval(() => {
      if (this.uploadProgress < 90) {
        this.uploadProgress += 1;
      }
    }, 100);
  }

  private clearUploadInterval(): void {
    if (this.uploadInterval) {
      clearInterval(this.uploadInterval);
      this.uploadInterval = undefined;
    }
  }

  private getDocumentName(): string {
    return this.isOtherSelected
      ? this.toTitleCase(this.otherDocumentName)
      : this.selectedDocumentType!.name;
  }

  private getFileExtension(): string {
    const parts = this.fileName.split('.');
    return parts.length > 1 ? `.${parts.pop()}` : '';
  }

  private toTitleCase(value: string): string {
    return value
      .trim()
      .toLowerCase()
      .split(' ')
      .filter(Boolean)
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  }
}
